var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { Button, useTranslate, useEvent } from 'react-admin';
import { Tooltip, IconButton } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FormDialogContext } from './FormDialogContext';
/**
 * Internal component which creates a dialog, along with a `<Button>` to open it.
 * This component is also responsible for managing the open/close state of the Dialog
 * (using an internal state, not the router).
 *
 * @param props.dialog A React Element containing the dialog
 * @param props.inline Optional - Set to true for an inline button, having only an icon and a tooltip
 * @param props.icon Optional - The icon associated to the button label
 * @param props.label Optional - The button label
 * @param props.ButtonProps Optional - An object containing props to pass to the MUI Button
 */
export var FormDialogButton = function (props) {
    var translate = useTranslate();
    var _a = useState(false), isOpen = _a[0], setIsOpen = _a[1];
    var onCloseCallbackRef = useRef();
    var open = useEvent(function () {
        setIsOpen(true);
    });
    var close = useEvent(function () { return __awaiter(void 0, void 0, void 0, function () {
        var canClose;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    canClose = true;
                    if (!onCloseCallbackRef.current) return [3 /*break*/, 3];
                    // Wait next tick to support pessimistic mode
                    return [4 /*yield*/, new Promise(function (res) { return setTimeout(res, 0); })];
                case 1:
                    // Wait next tick to support pessimistic mode
                    _a.sent();
                    return [4 /*yield*/, onCloseCallbackRef.current()];
                case 2:
                    canClose = _a.sent();
                    _a.label = 3;
                case 3:
                    if (canClose) {
                        setIsOpen(false);
                    }
                    return [2 /*return*/];
            }
        });
    }); });
    var registerOnCloseCallback = useEvent(function (onCloseCallback) {
        onCloseCallbackRef.current = onCloseCallback;
    });
    var contextValue = useMemo(function () { return ({
        isOpen: isOpen,
        open: open,
        close: close,
        registerOnCloseCallback: registerOnCloseCallback,
    }); }, [close, isOpen, open, registerOnCloseCallback]);
    var _b = props.icon, icon = _b === void 0 ? defaultIcon : _b, _c = props.label, label = _c === void 0 ? '' : _c, inline = props.inline, dialog = props.dialog, ButtonProps = props.ButtonProps;
    var _d = ButtonProps || {}, onClickProp = _d.onClick, restButtonProps = __rest(_d, ["onClick"]);
    var onClick = useEvent(function (e) {
        if (onClickProp) {
            onClickProp(e);
        }
        open();
        e.stopPropagation();
    });
    var translatedLabel = translate(label, { _: label });
    var button = inline ? (_jsx(Tooltip, { title: translatedLabel, children: _jsx(IconButton, __assign({ "aria-label": translatedLabel, size: "small", color: "primary" }, restButtonProps, { onClick: onClick, children: icon })) })) : (_jsx(Button, __assign({ label: translatedLabel }, restButtonProps, { onClick: onClick, children: icon })));
    return (_jsxs(FormDialogContext.Provider, { value: contextValue, children: [button, dialog] }));
};
var defaultIcon = _jsx(OpenInNewIcon, {});
